import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';

import {
  countryNameOptions,
  TCountryNameOption,
} from '../../../../../../constants';
import {
  adminCreateStudentFlexibleRate,
  adminUpdateStudentFlexibleRate,
  IStudentFlexibleRate,
  TAdminCreateStudentFlexibleRate,
  TAdminUpdateStudentFlexibleRate,
} from '../../../../../../store/V2/StudentFlexibleRatesSlice';
import { IFlexiblePlan } from '../../../../../../store/V2/FlexiblePlansSlice';
import { useAppSelector } from '../../../../../../hooks';
import { getCurriculumV2State } from '../../../../../../store/V2/CurriculumSlice';
import { IStudentFlexibleRateVersion } from '../../../../../../store/V2/StudentFlexibleRateVersionsSlice';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import FlexiblePlanListDropDown, {
  TFlexiblePlanOption,
} from '../../../../../../components/Inputs/admin/FlexiblePlanListDropDown';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import { TStudentFlexibleRateVersionOption } from '../../../../../../components/Inputs/admin/StudentFlexibleRateVersionListDropDown';
import AddAmountModal from './AddAmountModal';

interface AddStudentFlexibleRateModalProps {
  open: boolean;
  onClose: () => void;
  studentFlexibleRate?: IStudentFlexibleRate;
  readOnly?: boolean;
  duplicate?: boolean;
}

export type TAmountOption = {
  package: TStudentFlexibleRateVersionOption;
  amount: number;
  currency: string;
};

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedPlan: TFlexiblePlanOption | null;
  selectedCountry: TCountryNameOption | null;
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedAmounts: TAmountOption[];
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  selectedPlan: null,
  selectedCountry: null,
  selectedCurriculum: null,
  selectedGrade: null,
  selectedAmounts: [],
};

const AddStudentFlexibleRateModal: FunctionComponent<AddStudentFlexibleRateModalProps> = ({
  open,
  onClose,
  studentFlexibleRate,
  readOnly = false,
  duplicate = false,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // Store
  const curriculumState = useAppSelector(getCurriculumV2State);

  // State
  const [state, setState] = useState({ ...initialState });
  const [isAddAmountModalOpen, setAddAmountModalOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);
  const [editAmountModalData, setEditAmountModalData] = useState<{
    open: boolean;
    amountData?: TAmountOption;
    itemIndex?: number;
  }>({ open: false });

  let mode: 'new' | 'edit' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Flexible Rate';

  const isSubmitDisabled = Boolean(
    !state.selectedPlan || !state.selectedAmounts.length
  );

  if (studentFlexibleRate) {
    mode = 'edit';
    modalTitle = 'Edit Flexible Rate';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Flexible Rate Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Flexible Rate';
    }
  }

  useEffect(() => {
    let _selectedCurriculum: TSelectOption | undefined;

    if (mode !== 'new' && studentFlexibleRate) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (studentFlexibleRate.package) {
          const _packageData = studentFlexibleRate.package as IFlexiblePlan;
          newStateData.selectedPlan = {
            label: _packageData.name,
            value: _packageData._id,
          };
        }

        if (studentFlexibleRate.country) {
          const matchingCountry = countryNameOptions.find(
            (item) => item.value === studentFlexibleRate.country
          );

          if (matchingCountry) {
            newStateData.selectedCountry = matchingCountry;
          }
        }

        if (studentFlexibleRate.curriculum) {
          _selectedCurriculum = {
            label: studentFlexibleRate.curriculum,
            value: studentFlexibleRate.curriculum,
          };

          newStateData.selectedCurriculum = _selectedCurriculum;
        }

        if (studentFlexibleRate.grade) {
          newStateData.selectedGrade = {
            label: studentFlexibleRate.grade,
            value: studentFlexibleRate.grade,
          };
        }

        if (studentFlexibleRate.amounts && studentFlexibleRate.amounts.length) {
          newStateData.selectedAmounts = studentFlexibleRate.amounts.map(
            (item) => {
              const studentFlexibleRateData = item.studentFlexibleRateVersion as IStudentFlexibleRateVersion;

              return {
                amount: item.amount,
                currency: item.currency,
                package: {
                  label: studentFlexibleRateData.name,
                  value: studentFlexibleRateData._id,
                },
              };
            }
          );
        }

        return newStateData;
      });
    }

    if (
      curriculumState.curriculumsWithGradesList &&
      curriculumState.curriculumsWithGradesList.length
    ) {
      setCurriculumOptions(
        curriculumState.curriculumsWithGradesList.map((curriculumItem) => ({
          label: curriculumItem.name,
          value: curriculumItem.name,
        }))
      );

      if (_selectedCurriculum) {
        const matchingCurriculum = curriculumState.curriculumsWithGradesList.find(
          (item) => item.name === _selectedCurriculum?.value
        );

        if (
          matchingCurriculum &&
          matchingCurriculum.grades &&
          matchingCurriculum.grades.length
        ) {
          setGradeOptions(
            matchingCurriculum.grades.map((item) => ({
              label: item.grade,
              value: item.grade,
            }))
          );
        }
      }
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const addAmount = (amountData: TAmountOption) => {
    setState((prevState) => {
      const newState = { ...prevState };
      const newSelectedAmounts = [...prevState.selectedAmounts];

      if (
        newSelectedAmounts.some(
          (item) => item.package.value === amountData.package.value
        )
      ) {
        confirm({
          title: null,
          description: 'Rate version already added.',
          hideCancelButton: true,
        });
      } else {
        newSelectedAmounts.push(amountData);
      }

      newState.selectedAmounts = newSelectedAmounts;

      return newState;
    });
  };

  const updateAmount = (amountData: TAmountOption) => {
    let itemIndex = -1;

    if (
      editAmountModalData &&
      editAmountModalData.itemIndex !== undefined &&
      editAmountModalData.itemIndex > -1
    ) {
      itemIndex = editAmountModalData.itemIndex;
    }

    if (itemIndex > -1) {
      setState((prevState) => {
        const newState = { ...prevState };
        const newSelectedAmounts = [...prevState.selectedAmounts];

        const isRateVersionExists = newSelectedAmounts
          .filter((item, index) => index !== itemIndex)
          .some((item) => item.package.value === amountData.package.value);

        if (isRateVersionExists) {
          confirm({
            title: null,
            description: 'Rate version already added.',
            hideCancelButton: true,
          });
        } else {
          newSelectedAmounts[itemIndex] = { ...amountData };
        }

        newState.selectedAmounts = newSelectedAmounts;

        return newState;
      });
    }
  };

  const removeAmount = (amountData: TAmountOption) => {
    setState((prevState) => {
      const newState = { ...prevState };
      const newSelectedAmounts = [...prevState.selectedAmounts].filter(
        (item) => item !== amountData
      );

      newState.selectedAmounts = newSelectedAmounts;

      return newState;
    });
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    updateStateAttributes({
      selectedCurriculum: value,
      selectedGrade: null,
    });

    if (
      curriculumState.curriculumsWithGradesList &&
      curriculumState.curriculumsWithGradesList.length &&
      value
    ) {
      const selectedCurriculumData = curriculumState.curriculumsWithGradesList.find(
        (item) => item.name === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.grades &&
        selectedCurriculumData.grades.length
      ) {
        setGradeOptions(
          selectedCurriculumData.grades.map((item) => ({
            label: item.grade,
            value: item.grade,
          }))
        );
      }
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      if (state.selectedPlan) {
        const apiData: TAdminCreateStudentFlexibleRate = {
          package: state.selectedPlan.value,
          amounts: state.selectedAmounts.map((item) => ({
            studentFlexibleRateVersion: item.package.value,
            amount: item.amount,
            currency: item.currency,
          })),
        };

        if (state.selectedCountry) {
          apiData.country = state.selectedCountry.value;
        }

        if (state.selectedCurriculum) {
          apiData.curriculum = state.selectedCurriculum.value;

          if (state.selectedGrade) {
            apiData.grade = state.selectedGrade.value;
          }
        }

        const res: any = await dispatch(
          adminCreateStudentFlexibleRate(apiData)
        );

        if (res.payload) {
          onClose();
        }
      }
    } else if (mode === 'edit' && studentFlexibleRate) {
      if (state.selectedPlan) {
        const apiData: TAdminUpdateStudentFlexibleRate = {
          id: studentFlexibleRate._id,
          package: state.selectedPlan.value,
          amounts: state.selectedAmounts.map((item) => ({
            studentFlexibleRateVersion: item.package.value,
            amount: item.amount,
            currency: item.currency,
          })),
        };

        if (state.selectedCountry) {
          apiData.country = state.selectedCountry.value;
        }

        if (state.selectedCurriculum) {
          apiData.curriculum = state.selectedCurriculum.value;

          if (state.selectedGrade) {
            apiData.grade = state.selectedGrade.value;
          }
        }

        const res: any = await dispatch(
          adminUpdateStudentFlexibleRate(apiData)
        );

        if (res.payload) {
          onClose();
        }
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FlexiblePlanListDropDown
                  selectedItem={state.selectedPlan}
                  handleChange={(event, value) =>
                    updateStateAttributes({ selectedPlan: value })
                  }
                  componentProps={{ readOnly, fullWidth: true }}
                  textFieldProps={{
                    label: 'Flexible Plan *',
                    placeholder: 'Select',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  readOnly={readOnly}
                  value={state.selectedCountry}
                  options={countryNameOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  onChange={(e, value) =>
                    updateStateAttributes({ selectedCountry: value })
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Country"
                      placeholder="Select"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  readOnly={readOnly}
                  value={state.selectedCurriculum}
                  options={curriculumOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  getOptionKey={(option: TSelectOption) => option.value}
                  onChange={handleCurriculumChange}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Curriculum"
                      placeholder="Select"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  readOnly={readOnly}
                  disabled={!state.selectedCurriculum}
                  value={state.selectedGrade}
                  options={gradeOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  getOptionKey={(option: TSelectOption) => option.value}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  onChange={(event, value) =>
                    updateStateAttributes({ selectedGrade: value })
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Grade"
                      placeholder="Select"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            {mode !== 'view' ? (
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end">
                  <DarkButton
                    sx={{ ...defaultButtonStyles }}
                    onClick={() => setAddAmountModalOpen(true)}
                  >
                    Add New Amount
                  </DarkButton>
                </Stack>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <div>
                <Typography fontSize="14px" fontWeight={600}>
                  Amounts *
                </Typography>
              </div>
            </Grid>
            {state.selectedAmounts.map((item, index) => {
              return (
                <Grid item xs={12} key={`amount-${index}`}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography fontSize="14px" fontWeight={500}>
                      {item.package.label} - {item.currency}&nbsp;
                      {item.amount}
                    </Typography>
                    {mode !== 'view' ? (
                      <Stack direction="row" alignItems="center">
                        <IconButton
                          onClick={() =>
                            setEditAmountModalData({
                              open: true,
                              amountData: item,
                              itemIndex: index,
                            })
                          }
                        >
                          <EditIcon sx={{ color: '#1E293B' }} />
                        </IconButton>
                        <IconButton onClick={() => removeAmount(item)}>
                          <DeleteIcon sx={{ color: '#1E293B' }} />
                        </IconButton>
                      </Stack>
                    ) : null}
                  </Stack>
                </Grid>
              );
            })}
            {!state.selectedAmounts.length ? (
              <Grid item xs={12}>
                <div>
                  <Typography fontSize="14px" fontWeight={500}>
                    No amounts are available
                  </Typography>
                </div>
              </Grid>
            ) : null}
          </Grid>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
      {isAddAmountModalOpen ? (
        <AddAmountModal
          open={isAddAmountModalOpen}
          onClose={() => setAddAmountModalOpen(false)}
          onAddAmount={addAmount}
        />
      ) : null}
      {editAmountModalData && editAmountModalData.amountData ? (
        <AddAmountModal
          open={editAmountModalData.open}
          onClose={() => setEditAmountModalData({ open: false })}
          amountData={editAmountModalData.amountData}
          onUpdateAmount={updateAmount}
        />
      ) : null}
    </Dialog>
  );
};

export default AddStudentFlexibleRateModal;
